import React from "react";
import { styled } from "@mui/material/styles";
import { useTheme } from "@emotion/react";
import useMediaQuery from "@mui/material/useMediaQuery";
import ChevronLeft from "@mui/icons-material/ChevronLeft";

import IconButton from "@vapor/react-material/IconButton";
import CssBaseline from "@vapor/react-material/CssBaseline";
import Box from "@vapor/react-material/Box";
import Drawer from "@vapor/react-material/Drawer";

let warnedOnce = false;

const warn = () => {
  if (!warnedOnce) {
    console.warn(
      [
        "Vapor: The VaporUIShell component will be moved from `@vapor/react-custom` to `@vapor/react-uishell`.",
        "",
        "For more information you may want to check Vapor UIShell CHANGELOG https://biosphere.teamsystem.com/vapor/react/vapor-uishell/-/blob/develop/CHANGELOG.md"
      ].join("\n")
    );
  }

  warnedOnce = true;
};

type UIShellProps = {
  children?: React.ReactNode;
  renderToolbar?: React.ReactNode;
  renderDrawer?: React.ReactNode;
  drawerOpen?: boolean;
  setDrawerOpen?: Function;
  hideDrawer?: boolean;
};

const drawerWidth = 248;

const openedMixin = (theme) => ({
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen
  }),
  overflowX: "hidden"
});

const closedMixin = (theme) => ({
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen
  }),
  overflowX: "hidden",
  width: `calc(${theme.spacing(6)} + 1px)`,
  [theme.breakpoints.up("sm")]: {
    width: `calc(${theme.spacing(6)} + 1px)`
  }
});

const DrawerXl = styled(Drawer, {
  shouldForwardProp: (prop) => prop !== "open"
})<{ open?: boolean }>(({ theme, open }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: "nowrap",
  boxSizing: "border-box",
  display: "none",

  ...((open && {
    ...openedMixin(theme),
    "& .MuiDrawer-paper": openedMixin(theme),
    "& .MuiDrawer-paperAnchorLeft": {
      width: "248px !important"
    }
  }) as any),
  ...((!open && {
    ...closedMixin(theme),
    "& .MuiDrawer-paper": closedMixin(theme),
    "& .MuiDrawer-paperAnchorLeft": {
      width: "48px"
    }
  }) as any)
}));

export const VaporUIShellNav = styled(
  (props: UIShellProps) => {
    const { renderToolbar, renderDrawer, hideDrawer } = props;
    const theme: any = useTheme();
    const isLarge = useMediaQuery(theme.breakpoints.up("lg"));

    React.useEffect(() => {
      hideDrawer && props.setDrawerOpen(false);
    }, []);

    const [drawerOpen, setDrawerOpen] = [props.drawerOpen, props.setDrawerOpen];

    const transformRec = (ob, parent = undefined) => {
      ob.parent = parent;

      if (ob?.children) {
        for (let i = 0; i < ob.children.length; i++) {
          transformRec(ob.children[i], ob);
        }
      }
      return ob;
    };

    const handleDrawer = () => {
      setDrawerOpen(!drawerOpen);
    };

    const toggleDrawer = () => {
      setDrawerOpen(!drawerOpen);
    };

    warn();

    return (
      <Box>
        <Box sx={{ display: "flex" }}>
          <CssBaseline />
          {renderToolbar && renderToolbar}
          <DrawerXl
            variant="permanent"
            open={drawerOpen}
            sx={{
              display: hideDrawer ? "none" : { xs: "none", lg: "block" }
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-end",
                padding: theme.spacing(0, 1),
                height: 48
              }}
            >
              <IconButton onClick={toggleDrawer}>
                <ChevronLeft />
              </IconButton>
            </Box>
            <Box>{renderDrawer && renderDrawer}</Box>
          </DrawerXl>
          <Drawer
            anchor="left"
            // variant={isLarge ? "permanent" : "temporary"}
            variant="temporary"
            ModalProps={{
              onBackdropClick: handleDrawer,
              BackdropProps: {
                sx: { background: "rgba(0, 70, 102, 0.32) !important" }
              }
            }}
            sx={{
              flexShrink: 0,
              "&.MuiDrawer-root": {
                // width: isLarge && "calc(48px + 1px)"
              },
              "& .MuiDrawer-paper": {
                boxSizing: "border-box"
              },
              display: hideDrawer ? "none" : { xs: "block", lg: "none" },
              "& .MuiDrawer-paperAnchorLeft": {
                marginTop: "48px"
              }
            }}
            open={isLarge ? false : drawerOpen}
          >
            <Box>{props.renderDrawer && props.renderDrawer}</Box>
          </Drawer>
          <Box sx={{ flexGrow: 1, mt: "48px" }}>{props.children}</Box>
        </Box>
      </Box>
    );
  },
  {
    name: "VaporUIShellNav",
    slot: "Root",
    overridesResolver: (props, styles) => []
  }
)(({ theme }) => ({}));
